import { IonApp, setupIonicReact } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Redirect } from "react-router";

import { isMaintenanceMode } from "@/utils/constants/maintenance";
import { ROUTING } from "@/utils/constants/routing";
import { lazyNamed } from "@/utils/lazyNamed";

import { AutoSideBar } from "./components/navbar/default-sidebar/SideBarComponent";
import {
  LoggedInRoute,
  LoggedOutRoute,
} from "./components/routing/LoggedInRoute";
import { RouteSuspense } from "./components/routing/RouteSuspense";
import { TypeSafeRoute } from "./components/routing/TypeSafeRouting";
import { ApiTokenProvider } from "./contexts/api-token/ApiContextWrapper";
import { AuthApiProvider } from "./contexts/authenticated-api/AuthApiProvider";
import { LoadingProvider } from "./contexts/loading-page/LoadingPageProvider";
import { UserProvider } from "./contexts/user/UserContext";
import { ForgotPasswordPage } from "./pages/login/ForgotPassword";
import { LoginPage } from "./pages/login/Login";
import { SiteMaintenancePage } from "./pages/maintenance/SiteMaintenancePage";
import { MarketingPage } from "./pages/marketing/MarketingPage";
import { OwnerProfileGate } from "./pages/onboarding/OwnerProfileGate";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/react/css/display.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/padding.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";

/**
 * Ionic Dark Mode
 * -----------------------------------------------------
 * For more info, please see:
 * https://ionicframework.com/docs/theming/dark-mode
 */

// import "@ionic/react/css/palettes/dark.always.css";
/* Import '@ionic/react/css/palettes/dark.class.css'; */
// import "@ionic/react/css/palettes/dark.system.css";

/* Theme variables */

import "./theme/global.css";
import "./theme/TableStyles.css";
import "./theme/variables.css";

const queryClient = new QueryClient();

const CheckoutSuccessPage = lazyNamed(
  () => import("./pages/checkout/CheckoutSuccessPage"),
  "CheckoutSuccessPage",
);
const CheckoutRevertPage = lazyNamed(
  () => import("./pages/checkout/CheckoutRevertPage"),
  "CheckoutRevertPage",
);
const DashboardRouting = lazyNamed(
  () => import("./pages/dashboard/DashboardRouting"),
  "DashboardRouting",
);
const FaqPage = lazyNamed(() => import("./pages/FAQ/FaqPage"), "FaqPage");
const RegisterPage = lazyNamed(
  () => import("./pages/login/Register"),
  "RegisterPage",
);
const RegisterJoinPage = lazyNamed(
  () => import("./pages/login/RegisterJoin"),
  "RegisterJoinPage",
);
const ResetPasswordPage = lazyNamed(
  () => import("./pages/login/ResetPassword"),
  "ResetPasswordPage",
);
const OrganizationSetupPage = lazyNamed(
  () => import("./pages/onboarding/OrganizationSetupPage"),
  "OrganizationSetupPage",
);

setupIonicReact({ innerHTMLTemplatesEnabled: true });
function AppRoutes() {
  return (
    <QueryClientProvider client={queryClient}>
      <ApiTokenProvider>
        <AuthApiProvider>
          <UserProvider>
            <LoadingProvider>
              <IonReactRouter>
                <OwnerProfileGate>
                  <AutoSideBar />
                  <RouteSuspense>
                    <TypeSafeRoute
                      exact
                      path={ROUTING.HOME}
                      component={MarketingPage}
                    />
                    <TypeSafeRoute
                      exact
                      path={ROUTING.ROOT}
                      component={() => <Redirect to={ROUTING.HOME} />}
                    />
                    <TypeSafeRoute
                      exact
                      path={ROUTING.LOGIN}
                      component={LoginPage}
                    />
                    <LoggedOutRoute
                      exact
                      path={ROUTING.FORGOT_PASSWORD}
                      defaultTo={ROUTING.DASHBOARD.ROOT}
                      component={ForgotPasswordPage}
                    />
                    <LoggedOutRoute
                      exact
                      path={ROUTING.RESET_PASSWORD}
                      defaultTo={ROUTING.DASHBOARD.ROOT}
                      component={ResetPasswordPage}
                    />
                    <TypeSafeRoute
                      exact
                      path={ROUTING.CHECKOUT_SUCCESS}
                      component={CheckoutSuccessPage}
                    />
                    <TypeSafeRoute
                      exact
                      path={ROUTING.CHECKOUT_CANCEL}
                      component={CheckoutRevertPage}
                    />
                    <TypeSafeRoute
                      exact
                      path={ROUTING.FAQ}
                      component={FaqPage}
                    />
                    <LoggedOutRoute
                      exact
                      path={ROUTING.REGISTER}
                      defaultTo={ROUTING.DASHBOARD.ROOT}
                      component={RegisterPage}
                    />
                    <LoggedOutRoute
                      exact
                      path={ROUTING.REGISTER_JOIN}
                      defaultTo={ROUTING.DASHBOARD.ROOT}
                      component={RegisterJoinPage}
                    />
                    <LoggedInRoute
                      exact
                      path={ROUTING.ONBOARDING_ORGANIZATION}
                      defaultTo={ROUTING.HOME}
                      component={OrganizationSetupPage}
                    />
                    <LoggedInRoute
                      path={ROUTING.DASHBOARD.ROOT}
                      defaultTo={ROUTING.HOME}
                      component={DashboardRouting}
                    />
                  </RouteSuspense>
                </OwnerProfileGate>
              </IonReactRouter>
            </LoadingProvider>
          </UserProvider>
        </AuthApiProvider>
      </ApiTokenProvider>
    </QueryClientProvider>
  );
}
function App() {
  if (isMaintenanceMode()) {
    return (
      <IonApp>
        <SiteMaintenancePage />
      </IonApp>
    );
  }

  return (
    <IonApp>
      <AppRoutes />
    </IonApp>
  );
}
export default App;
